<script setup>
import { computed } from 'vue';
import BaseBubble from './Base.vue';
import { useI18n } from 'vue-i18n';
import { CONTENT_TYPES } from '../constants.js';
import { useMessageContext } from '../provider.js';
import { useInbox } from 'dashboard/composables/useInbox';

const { content, contentAttributes, contentType, messageType } =
  useMessageContext();
const { t } = useI18n();
const { isAWebWidgetInbox } = useInbox();

const formValues = computed(() => {
  if (contentType.value === CONTENT_TYPES.FORM) {
    const { items, submittedValues = [] } = contentAttributes.value;

    if (submittedValues.length) {
      return submittedValues.map(submittedValue => {
        const item = items.find(
          formItem => formItem.name === submittedValue.name
        );
        return {
          title: submittedValue.value,
          value: submittedValue.value,
          label: item?.label,
        };
      });
    }

    return [];
  }

  if (contentType.value === CONTENT_TYPES.INPUT_SELECT) {
    const [item] = contentAttributes.value?.submittedValues ?? [];
    if (!item) return [];

    return [
      {
        title: item.title,
        value: item.value,
        label: '',
      },
    ];
  }

  return [];
});

// Add computed property for interactive message options (outgoing messages)
const interactiveOptions = computed(() => {
  if (
    contentType.value === CONTENT_TYPES.INPUT_SELECT &&
    messageType.value === 1
  ) {
    // 1 = OUTGOING
    const attrs = contentAttributes.value || {};

    // Handle sections format (Facebook Button Template, List, etc.)
    if (attrs.sections) {
      return attrs.sections.flatMap(section =>
        section.rows.map(row => ({
          title: row.title,
          description: row.description,
          value: row.title,
        }))
      );
    }

    // Handle items format (Quick Replies, etc.)
    const { items = [] } = attrs;
    return items.map(item => ({
      title: item.title,
      description: item.description,
      value: item.value,
    }));
  }
  return [];
});

const isOutgoingInteractiveMessage = computed(() => {
  return (
    contentType.value === CONTENT_TYPES.INPUT_SELECT &&
    messageType.value === 1 &&
    interactiveOptions.value.length > 0
  );
});
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="csat">
    <span v-dompurify-html="content" :title="content" />

    <!-- Show interactive options for outgoing messages -->
    <div v-if="isOutgoingInteractiveMessage" class="mt-4">
      <div class="text-sm text-n-slate-11 mb-2">
        {{ t('CONVERSATION.INTERACTIVE_OPTIONS') }}
      </div>
      <div class="space-y-2">
        <div
          v-for="option in interactiveOptions"
          :key="option.value"
          class="px-3 py-2 border border-n-slate-6 rounded-lg bg-n-slate-1"
        >
          <div class="font-medium text-sm text-n-slate-12">
            {{ option.title }}
          </div>
          <div v-if="option.description" class="text-xs text-n-slate-10 mt-0.5">
            {{ option.description }}
          </div>
        </div>
      </div>
    </div>

    <!-- Show submitted values for incoming responses -->
    <dl v-else-if="formValues.length" class="mt-4">
      <template v-for="item in formValues" :key="item.title">
        <dt class="text-n-slate-11 italic mt-2">
          {{ item.label || t('CONVERSATION.RESPONSE') }}
        </dt>
        <dd>{{ item.title }}</dd>
      </template>
    </dl>

    <!-- Show no response message for web widget -->
    <div v-else-if="isAWebWidgetInbox" class="my-2 font-medium">
      {{ t('CONVERSATION.NO_RESPONSE') }}
    </div>
  </BaseBubble>
</template>
